'use client'

import { useCallback, useEffect, useRef, useState } from 'react'
import { ChevronDown, Menu } from 'lucide-react'
import { cn } from '~/src/lib/utils'

interface TocEntry {
  id: string
  label: string
  depth?: number
}

interface SeoReportTableOfContentsProps {
  entries: TocEntry[]
  variant?: 'mobile' | 'desktop'
}

export function SeoReportTableOfContents({
  entries,
  variant,
}: SeoReportTableOfContentsProps) {
  const [activeId, setActiveId] = useState<string>(entries[0]?.id ?? '')
  const [isOpen, setIsOpen] = useState(false)
  const observerRef = useRef<IntersectionObserver | null>(null)

  const handleObserver = useCallback(
    (observerEntries: IntersectionObserverEntry[]) => {
      for (const entry of observerEntries) {
        if (entry.isIntersecting) {
          setActiveId(entry.target.id)
        }
      }
    },
    []
  )

  useEffect(() => {
    observerRef.current = new IntersectionObserver(handleObserver, {
      rootMargin: '-80px 0px -60% 0px',
      threshold: 0,
    })

    const observer = observerRef.current

    for (const tocEntry of entries) {
      const element = document.getElementById(tocEntry.id)
      if (element) {
        observer.observe(element)
      }
    }

    return () => {
      observer.disconnect()
    }
  }, [entries, handleObserver])

  const handleClick = (id: string) => {
    setActiveId(id)
    setIsOpen(false)
    // Wait a frame for the dropdown to close and layout to settle before scrolling
    requestAnimationFrame(() => {
      document
        .getElementById(id)
        ?.scrollIntoView({ behavior: 'smooth', block: 'start' })
    })
  }

  return (
    <nav aria-label='Quick links' className='w-full'>
      {/* Mobile: sticky dropdown at top of page */}
      {variant !== 'desktop' && (
        <div className='lg:hidden'>
          <div className='border-b border-border bg-white'>
            <button
              type='button'
              onClick={() => setIsOpen((prev) => !prev)}
              className='flex w-full items-center justify-between px-4 py-3 text-sm font-semibold text-foreground'
              aria-expanded={isOpen}
            >
              <span className='flex items-center gap-2'>
                <Menu className='size-4' strokeWidth={2} />
                Quick links
              </span>
              <ChevronDown
                className={cn(
                  'size-4 transition-transform duration-200',
                  isOpen && 'rotate-180'
                )}
                strokeWidth={2}
              />
            </button>
            {isOpen && (
              <ul className='flex flex-col gap-1 px-4 pb-3'>
                {entries.map((entry) => (
                  <li key={entry.id}>
                    <a
                      href={`#${entry.id}`}
                      onClick={(e) => {
                        e.preventDefault()
                        handleClick(entry.id)
                      }}
                      className={cn(
                        'block truncate py-1 text-sm leading-5',
                        activeId === entry.id
                          ? 'font-medium text-info-700'
                          : 'text-ds-text-quaternary'
                      )}
                      title={entry.label}
                    >
                      {entry.label}
                    </a>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      )}

      {/* Desktop: sidebar list (sticky handled by parent) */}
      {variant !== 'mobile' && (
        <div className='hidden lg:block'>
          <p className='border-b border-border pb-3 text-base font-semibold text-foreground-secondary'>
            Quick links
          </p>
          <ul className='mt-3 flex flex-col gap-2'>
            {entries.map((entry) => (
              <li key={entry.id}>
                <a
                  href={`#${entry.id}`}
                  onClick={(e) => {
                    e.preventDefault()
                    handleClick(entry.id)
                  }}
                  className={cn(
                    'block truncate text-sm leading-5 transition-colors',
                    activeId === entry.id
                      ? 'font-medium text-info-700'
                      : 'text-ds-text-quaternary hover:text-ds-text-secondary'
                  )}
                  title={entry.label}
                >
                  {entry.label}
                </a>
              </li>
            ))}
          </ul>
        </div>
      )}
    </nav>
  )
}
